import { t } from "@lingui/macro"
import { useCallback, useMemo } from "react"
import { Tooltip, Box } from "@mui/material"

import { GridRenderCellParams } from "@mui/x-data-grid-pro"

import { AccessTypeHeader } from "src/components/AccessTypeHeader"
import { RowCellContainer } from "src/components/RowCellContainer"
import { StatusChip } from "src/components/StatusChip"
import { TableChipCell } from "src/components/TableChipCell"
import { IColumn, IGenericSingleSelectOptions } from "src/types/data-grid-pro"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"
import {
    KNOWLEDGE_TYPES,
    KNOWLEDGE_TYPE_DISPLAY_LABELS,
} from "src/shared/knowledge-base/constants"
import { tags_Tag } from "src/api"

// Helper function to get agent type display information
const getAgentTypeInfo = (agentTypeName: string) => {
    const agentTypeMap: Record<
        string,
        { label: string; color: "info" | "positive" | "neutral" | "other" }
    > = {
        qa_agent: { label: "Q&A", color: "info" },
        public_agent: { label: "Public", color: "positive" },
    }

    return (
        agentTypeMap[agentTypeName] ?? { label: agentTypeName, color: "other" }
    )
}

export const useKnowledgeBaseListColumns = (
    agentAccessTypes: IGenericSingleSelectOptions[],
    agentConfigs: Array<{
        value: number
        label: string
        agentType: string
        isInstanceRequired: boolean
    }>,
    tags: tags_Tag[],
    _isLibraryMode: boolean = false,
): IColumn<IKnowledgeItem>[] => {
    const renderTextCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>, fallback = "-") => {
            const value = Array.isArray(params.value)
                ? params.value.join(", ")
                : params.value
            return <RowCellContainer>{value ?? fallback}</RowCellContainer>
        },
        [],
    )

    const renderAccessTypeCell = useCallback(
        (params: GridRenderCellParams) => (
            <AccessTypeHeader accessType={params.value} />
        ),
        [],
    )

    const renderAgentTypesCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const agentTypeIds = params.row.agent_types ?? []
            const agentInstanceIds = params.row.agents ?? []

            // Get agent type names from direct type connections
            const directAgentTypes = agentTypeIds
                .map(
                    (id) =>
                        agentAccessTypes.find((type) => type.value === id)
                            ?.label,
                )
                .filter(Boolean) as string[]

            // Get agent types from instance connections
            const instanceAgentTypes = agentInstanceIds.flatMap((id) => {
                const config = agentConfigs.find(
                    (config) => config.value === id,
                )
                if (config !== null && config !== undefined) {
                    return [config.agentType]
                }
                return []
            })

            // Combine and deduplicate all agent types
            const allUniqueAgentTypes = Array.from(
                new Set([...directAgentTypes, ...instanceAgentTypes]),
            )

            if (allUniqueAgentTypes.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            if (allUniqueAgentTypes.length <= 2) {
                // Show chips when 2 or fewer
                return (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {allUniqueAgentTypes.map((agentType, index) => {
                            const typeInfo = getAgentTypeInfo(agentType)
                            return (
                                <StatusChip
                                    key={`type-${agentType}-${index}`}
                                    label={typeInfo.label}
                                    state={typeInfo.color}
                                    variant="filled"
                                />
                            )
                        })}
                    </Box>
                )
            }

            // Show count with tooltip when more than 2
            return (
                <Tooltip
                    title={
                        <Box display="flex" gap={0.5} flexWrap="wrap" p={0.5}>
                            {allUniqueAgentTypes.map((agentType, index) => {
                                const typeInfo = getAgentTypeInfo(agentType)
                                return (
                                    <StatusChip
                                        key={`type-${agentType}-${index}`}
                                        label={typeInfo.label}
                                        state={typeInfo.color}
                                        variant="filled"
                                    />
                                )
                            })}
                        </Box>
                    }
                    arrow
                >
                    <RowCellContainer>
                        {`(${
                            allUniqueAgentTypes.length
                        }) ${t`knowledge-base-list-view.agents`.toLowerCase()}`}
                    </RowCellContainer>
                </Tooltip>
            )
        },
        [agentAccessTypes, agentConfigs],
    )

    const renderTagsCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const tagIds = params.row.tags ?? []

            if (tagIds.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            // Map tag IDs to names
            const tagNames = tagIds
                .map((id) => tags.find((tag) => tag.tag_id === id)?.name)
                .filter(Boolean) as string[]

            if (tagNames.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            // Use TableChipCell for consistent display with information items
            return (
                <TableChipCell
                    value={Object.fromEntries(
                        tagNames.map((name, idx) => [idx, name]),
                    )}
                    label={t`tags-cell-component.tags`.toLowerCase()}
                />
            )
        },
        [tags],
    )

    const renderAvailabilityCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const isAvailable = params.row.is_available_for_chatbot

            return (
                <StatusChip
                    label={
                        isAvailable
                            ? t`knowledge-base-list-view.ready-for-use`
                            : t`knowledge-base-list-view.processing`
                    }
                    state={isAvailable ? "positive" : "neutral"}
                    variant="filled"
                />
            )
        },
        [],
    )

    const renderSegmentsCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const segmentNames = params.row.segment_names ?? []

            if (segmentNames.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            if (segmentNames.length <= 2) {
                // Show names when 2 or fewer
                return (
                    <RowCellContainer>
                        {segmentNames.join(", ")}
                    </RowCellContainer>
                )
            }

            // Show count with tooltip when more than 2
            return (
                <Tooltip title={segmentNames.join(", ")} arrow>
                    <RowCellContainer>
                        {`(${
                            segmentNames.length
                        }) ${t`knowledge-base-list-view.segments`.toLowerCase()}`}
                    </RowCellContainer>
                </Tooltip>
            )
        },
        [],
    )

    const renderKnowledgeTypeCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const typeValue = params.value

            // Find the matching type from KNOWLEDGE_TYPE_DISPLAY_LABELS to get the user-friendly label
            const matchingType = KNOWLEDGE_TYPE_DISPLAY_LABELS.find(
                (type) => type.value === typeValue,
            )

            const displayLabel = matchingType?.label ?? typeValue ?? "-"

            return <RowCellContainer>{displayLabel}</RowCellContainer>
        },
        [],
    )

    return useMemo(() => {
        const columns: IColumn<IKnowledgeItem>[] = [
            {
                field: "id",
                headerName: t`knowledge-base-list-view.columns.id`,
                renderCell: renderTextCell,
                type: "number",
                minWidth: 150,
                flex: 0.5,
                filterable: false,
                sortable: false,
            },
            {
                field: "title",
                headerName: t`knowledge-base-list-view.columns.title`,
                renderCell: renderTextCell,
                type: "string",
                minWidth: 200,
                flex: 1,
            },
        ]

        // Always show the knowledge_item_type column
        columns.push({
            field: "knowledge_item_type",
            headerName: t`knowledge-base-list-view.columns.knowledge-item-type`,
            renderCell: renderKnowledgeTypeCell,
            type: "singleSelect",
            minWidth: 130,
            flex: 1,
            // @ts-ignore
            // Value options is available on one of the options for type of TColumn(GridSingleSelectColDef), but it's not reading here
            valueOptions: KNOWLEDGE_TYPES,
        })

        columns.push(
            {
                field: "agent_types",
                headerName: t`knowledge-base-list-view.columns.agents`,
                renderCell: renderAgentTypesCell,
                type: "singleSelect",
                minWidth: 200,
                flex: 1.2,
                // @ts-ignore
                // Value options is available on one of the options for type of TColumn(GridSingleSelectColDef), but it's not reading here
                valueOptions: agentAccessTypes,
            },
            {
                field: "tags",
                headerName: t`knowledge-base-list-view.columns.tags`,
                renderCell: renderTagsCell,
                type: "string",
                minWidth: 150,
                flex: 1,
                sortable: false,
                filterable: false,
            },
            {
                field: "segment_names",
                headerName: t`knowledge-base-list-view.columns.segments`,
                renderCell: renderSegmentsCell,
                type: "string",
                minWidth: 180,
                flex: 1,
                sortable: false,
                filterable: false,
            },
            {
                field: "is_available_for_chatbot",
                headerName: t`knowledge-base-list-view.columns.status`,
                renderCell: renderAvailabilityCell,
                type: "string",
                minWidth: 120,
                flex: 0.8,
                sortable: false,
                filterable: false,
            },
            {
                field: "access_type",
                headerName: t`knowledge-base-list-view.columns.access-type`,
                renderCell: renderAccessTypeCell,
                type: "string",
                minWidth: 130,
                flex: 0.8,
                sortable: false,
                filterable: false,
            },
        )

        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        renderAccessTypeCell,
        renderAgentTypesCell,
        renderTagsCell,
        renderSegmentsCell,
        renderAvailabilityCell,
        renderTextCell,
        renderKnowledgeTypeCell,
        agentAccessTypes,
    ])
}
